import React from 'react'
import { motion } from 'framer-motion'
import { TrendingUp, TrendingDown, Minus } from 'lucide-react'
import Card from './Card'

interface KPICardProps {
  label: string
  value: string | number
  icon: React.ComponentType<{ className?: string }>
  color?: 'green' | 'blue' | 'purple' | 'orange' | 'pink' | 'indigo' | 'red' | 'yellow'
  change?: {
    value: string | number
    type: 'increase' | 'decrease' | 'neutral'
  }
  onClick?: () => void
  animation?: {
    initial?: any
    animate?: any
    transition?: any
    whileHover?: any
  }
  className?: string
}

const KPICard: React.FC<KPICardProps> = ({
  label,
  value,
  icon: Icon,
  color = 'blue',
  change,
  onClick,
  animation = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.3 },
    whileHover: { y: -5, scale: 1.02 }
  },
  className = ''
}) => {
  const getColorValue = (color: string): string => {
    const colorMap: Record<string, string> = {
      blue: '#3b82f6',
      green: '#10b981',
      purple: '#a855f7',
      orange: '#f97316',
      red: '#ef4444',
      indigo: '#6366f1',
      pink: '#ec4899',
      yellow: '#eab308'
    }
    return colorMap[color] || '#3b82f6'
  }

  const getColorClasses = (color: string) => {
    const colorMap = {
      green: 'bg-gradient-to-br from-green-500 to-emerald-600',
      blue: 'bg-gradient-to-br from-blue-500 to-blue-600',
      purple: 'bg-gradient-to-br from-purple-500 to-purple-600',
      orange: 'bg-gradient-to-br from-orange-500 to-orange-600',
      pink: 'bg-gradient-to-br from-pink-500 to-pink-600',
      indigo: 'bg-gradient-to-br from-indigo-500 to-indigo-600',
      red: 'bg-gradient-to-br from-red-500 to-red-600',
      yellow: 'bg-gradient-to-br from-yellow-500 to-yellow-600'
    }
    return colorMap[color as keyof typeof colorMap] || colorMap.blue
  }

  const getChangeIcon = (type: string) => {
    switch (type) {
      case 'increase':
        return <TrendingUp className="h-4 w-4 mr-1" />
      case 'decrease':
        return <TrendingDown className="h-4 w-4 mr-1" />
      default:
        return <Minus className="h-4 w-4 mr-1" />
    }
  }

  const getChangeColor = (type: string) => {
    switch (type) {
      case 'increase':
        return 'text-green-600'
      case 'decrease':
        return 'text-red-600'
      default:
        return 'text-gray-600'
    }
  }

  return (
    <Card onClick={onClick} animation={animation} className={className}>
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className={`p-3 rounded-xl shadow-lg ${getColorClasses(color)}`}>
            <Icon className="h-6 w-6 text-white" />
          </div>
          {change && (
            <div className={`flex items-center ${getChangeColor(change.type)}`}>
              {getChangeIcon(change.type)}
              <span className="text-sm font-semibold">{change.value}</span>
            </div>
          )}
        </div>
        <div>
          <p
            className="text-sm font-bold mb-1 bg-gradient-to-r bg-clip-text text-transparent"
            style={{
              backgroundImage: `linear-gradient(135deg, ${getColorValue(color)} 0%, ${getColorValue(color)}dd 100%)`
            }}
          >
            {label}
          </p>
          <p 
            className="text-2xl font-bold"
            style={{ color: 'var(--text-primary)' }}
          >
            {value}
          </p>
        </div>
      </div>
    </Card>
  )
}

export default KPICard
